import type { UserAssignment } from "@lexiun/org-admin";
import type {
  RawClientAssignmentRow,
  RawMatterAssignmentRow,
} from "./platform-api";

/**
 * Map a lexserver `ClientUserAssignmentEntry` (wire shape) to the
 * org-admin `UserAssignment` primitive shape. Direct rows set
 * `source: "direct"` and `scope: "client"`. To render a client
 * assignment as inherited under a matter/case view, use
 * `toInheritedFromClient()` below instead.
 */
export function clientRowToUserAssignment(
  row: RawClientAssignmentRow,
): UserAssignment {
  const result: UserAssignment = {
    assignmentId: row.assignmentId,
    scope: "client",
    scopeId: row.clientId,
    orgId: row.orgId,
    userId: row.userId,
    role: row.role,
    source: "direct",
    assignedBy: row.assignedBy,
    assignedAt: row.assignedAt,
    isActive: row.isActive,
  };
  if (row.expiresAt) result.expiresAt = row.expiresAt;
  return result;
}

/**
 * Map a lexserver `MatterUserAssignmentEntry` to the org-admin
 * direct-row shape at matter scope.
 */
export function matterRowToUserAssignment(
  row: RawMatterAssignmentRow,
): UserAssignment {
  const result: UserAssignment = {
    assignmentId: row.assignmentId,
    scope: "matter",
    scopeId: row.matterId,
    orgId: row.orgId,
    userId: row.userId,
    role: row.role,
    source: "direct",
    assignedBy: row.assignedBy,
    assignedAt: row.assignedAt,
    isActive: row.isActive,
  };
  if (row.expiresAt) result.expiresAt = row.expiresAt;
  return result;
}

/**
 * Render a client-level assignment as an inherited row when listing
 * matter or case assignments. The `scope` field on the returned row
 * is set to the VIEW scope (where the row is being displayed), not
 * the source scope — the source is captured in `inheritedFrom`.
 */
export function toInheritedFromClient(
  row: RawClientAssignmentRow,
  viewScope: "matter" | "case",
  viewScopeId: string,
  clientName?: string,
): UserAssignment {
  const inheritedFrom: UserAssignment["inheritedFrom"] = {
    scope: "client",
    scopeId: row.clientId,
  };
  if (clientName) inheritedFrom.scopeName = clientName;
  const result: UserAssignment = {
    // Prefix the assignmentId so React keys don't collide if the
    // same row is ever rendered twice in one tree (e.g. a future
    // combined view that lists the direct client row AND the same
    // row reflected into a child matter). Today's pages filter out
    // that case at the data layer, but the prefix keeps the
    // invariant defensible without relying on caller discipline.
    assignmentId: `inherited-client-${row.assignmentId}`,
    scope: viewScope,
    scopeId: viewScopeId,
    orgId: row.orgId,
    userId: row.userId,
    role: row.role,
    source: "inherited",
    inheritedFrom,
    assignedBy: row.assignedBy,
    assignedAt: row.assignedAt,
    isActive: row.isActive,
  };
  if (row.expiresAt) result.expiresAt = row.expiresAt;
  return result;
}
